/**
 * Cloud authentication handler for frontend.
 * Manages JWT token storage and authentication state.
 */

export interface User {
  id: number;
  email: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface AuthConfig {
  allowRegistration: boolean;
}

export type AuthEventType = 'login' | 'logout';

export interface AuthEventData {
  token?: string;
  user?: User;
}

export type AuthListener = (event: AuthEventType, data: AuthEventData) => void;

export class CloudAuth {
  private token: string | null;
  private user: User | null = null;
  private listeners: AuthListener[] = [];

  constructor() {
    // Use localStorage to persist session across tab closes
    this.token = localStorage.getItem('cloud_token');

    // Try to restore user info if we have a token
    if (this.token) {
      this.fetchUser().catch(() => {
        // Token is invalid, clear it
        this.logout();
      });
    }
  }

  /**
   * Register a new account
   */
  async register(email: string, password: string): Promise<AuthResponse> {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    this.setToken(data.token);
    this.user = data.user;
    this.notifyListeners('login', { token: data.token, user: data.user });

    return data;
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    this.setToken(data.token);
    this.user = data.user;
    this.notifyListeners('login', { token: data.token, user: data.user });

    return data;
  }

  /**
   * Logout - clear token and notify listeners
   */
  async logout(): Promise<void> {
    if (this.token) {
      // Invalidate session on server (fire and forget)
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        }
      }).catch(() => {});
    }

    localStorage.removeItem('cloud_token');
    this.token = null;
    this.user = null;
    this.notifyListeners('logout');
  }

  /**
   * Set token and save to localStorage
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('cloud_token', token);
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Get current user info
   */
  getUser(): User | null {
    return this.user;
  }

  /**
   * Fetch user info from server
   */
  async fetchUser(): Promise<User | null> {
    if (!this.token) return null;

    const res = await fetch('/api/auth/me', {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    if (!res.ok) {
      if (res.status === 401) {
        // Token is invalid, clear it
        await this.logout();
        return null;
      }
      throw new Error('Failed to fetch user');
    }

    const data = await res.json();
    this.user = data.user;
    return this.user;
  }

  /**
   * Register a callback for auth events
   */
  onAuthChange(callback: AuthListener): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: AuthEventType, data: AuthEventData = {}): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (err) {
        console.error('Auth listener error:', err);
      }
    });
  }

  /**
   * Fetch public auth config (e.g. whether registration is allowed)
   */
  async fetchConfig(): Promise<AuthConfig> {
    const res = await fetch('/api/auth/config');
    if (!res.ok) {
      return { allowRegistration: true };
    }
    return res.json();
  }

  /**
   * Validate current token
   */
  async validateToken(): Promise<boolean> {
    if (!this.token) return false;

    try {
      const user = await this.fetchUser();
      return !!user;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const cloudAuth = new CloudAuth();
